import React, { useMemo } from "react";
import { Card, CardBody, Heading, Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@aidkitorg/component-library";
import type { DeltaTargetFieldsWorkflowResult } from "@aidkitorg/aidkit/lib/distro/review/workflows/deltaTargetFieldsWorkflow";
import WorkflowReview from "./WorkflowReview";

export default function DeltaTargetFieldsWorkflowReview({ distroCommitId }: { distroCommitId: string }) {

  const renderDeltaTargetFieldsResults = (workflow: { result?: DeltaTargetFieldsWorkflowResult }) => {
    if (!workflow?.result?.success) {
      return null;
    }

    return <DeltaTargetFieldsResultsContent result={workflow.result} />;
  };

  return (
    <WorkflowReview
      distroCommitId={distroCommitId}
      workflowName="delta_target_fields"
      title="Delta Check"
      badge={{ label: 'Unsampled, changes only', variant: 'neutral' }}
      renderSummary={renderDeltaTargetFieldsResults}
    />
  );
}

function DeltaTargetFieldsResultsContent({ result }: { result: DeltaTargetFieldsWorkflowResult }) {
  // Only fields the simulation would change appear in the result at all.
  const fields = useMemo(() => result.targetFields || [], [result.targetFields]);

  return (
    <div className="space-y-6">
      {/* Summary */}
      <Card className="max-w-5xl">
        <CardBody>
          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt className="font-medium">Fields Affected</dt>
              <dd><span className="font-bold text-orange-600">{fields.length.toLocaleString()}</span></dd>
            </div>
            <div>
              <dt className="font-medium">Applicants Affected</dt>
              <dd><span className="font-bold text-orange-600">{result.changedApplicants.toLocaleString()}</span></dd>
            </div>
          </dl>
          <div className="mt-3 text-sm text-gray-600 border-t pt-3">
            This is a fast first look across every applicant in the program. See the "Target Fields"
            tab for a statistically sampled, complete before/after comparison once it's ready.
          </div>
        </CardBody>
      </Card>

      {/* Field Changes Details */}
      {fields.length > 0 && (
        <Card className="max-w-5xl">
          <CardBody>
            <Heading level="h3" className="text-md font-semibold mb-4" id="field-changes-details">
              Value Changes
            </Heading>
            <div className="space-y-3">
              {fields.map((field) => (
                <div
                  key={field.key}
                  className="border rounded-2xl overflow-hidden border-amber-200"
                >
                  {/* Field Header - Always Visible */}
                  <div className="flex justify-between items-center px-4 py-3 bg-gray-50">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-md">{field.key}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-medium text-xl">
                        {field.countChangedApplicants.toLocaleString()}
                      </span>
                      <div className="text-xs text-gray-500">affected applicants</div>
                    </div>
                  </div>

                  {field.beforeAfterValues && field.beforeAfterValues.length > 0 && (
                    <Accordion type="single">
                      <AccordionItem value={`field-${field.key}`} className="border-0">
                        <AccordionTrigger className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-t border-gray-200 flex justify-center">
                          <span>{field.countDistinctChanges.toLocaleString()} change pattern{field.countDistinctChanges !== 1 ? 's' : ''}</span>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-3 bg-white border-t border-gray-100">
                          <div className="pt-2">
                            <table className="text-sm table-auto">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th scope="col" className="text-left py-2 pr-4 font-medium text-gray-600">Before</th>
                                  <th scope="col" className="text-left py-2 pr-4 font-medium text-gray-600">After</th>
                                  <th scope="col" className="text-right py-2 pl-4 font-medium text-gray-600">Applicants</th>
                                </tr>
                              </thead>
                              <tbody>
                                {field.beforeAfterValues.map((change, index) => (
                                  <tr key={index} className="border-b border-gray-100">
                                    <td className="py-2 pr-4">
                                      {change.before !== null ? <div className="font-mono text-sm px-2 py-1 bg-orange-100 border border-orange-200 rounded min-w-6 max-w-fit min-h-7">
                                        {change.before}
                                      </div> : <span className="font-mono text-sm px-1 py-1">
                                        undefined
                                      </span>}
                                    </td>
                                    <td className="py-2 pr-4">
                                      {change.after !== null ? <div className="font-mono text-sm px-2 py-1 bg-blue-100 border border-blue-200 rounded min-w-6 max-w-fit min-h-7">
                                        {change.after}
                                      </div> : <span className="font-mono text-xs px-1 py-1">
                                        undefined
                                      </span>}
                                    </td>
                                    <td className="py-2 text-right pl-4">
                                      <span className="text-base">{change.count.toLocaleString()}</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {field.countDistinctChanges > field.beforeAfterValues.length && (
                              <div className="text-xs text-gray-500">
                                truncated to the {field.beforeAfterValues.length} most common changes
                              </div>
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {fields.length === 0 && (
        <Card className="max-w-5xl">
          <CardBody>
            <div className="text-sm text-gray-600">
              The simulation would not change any target field values.
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};
